import type { Ref } from 'react';
import type { SelectOption } from '@geberit/gdds/dist/esm/form/Dropdown/types/Dropdown.types';
import type { Data } from 'components/Filter/SumAndSortArea';

type Option = {
  title: string;
  value: string;
};

type Rule = {
  errorMessage: string;
  fieldName: string;
  formValidator: string;
  pattern: null;
};

type FieldsetField = {
  defaultValue: string;
  fieldType: string;
  id: string;
  label: string;
  name: string;
  options: Option[];
  placeholder: string;
  readonly: boolean;
  rules: Rule[];
  size: string;
  type: string;
  value: string;
};

type Property = {
  attachments: [];
  body: string;
  fileUploads: [];
  sendUserAttachments: boolean;
  smtp: null;
  subject: string;
  to: string;
};

type Form = {
  backgroundColor: string;
  border: string;
  date: Date;
  fieldsets: {
    fields: FieldsetField[];
  };
  formId: string;
  isInSubscription: null;
  language: string;
  pageId: string;
  preconditions: {
    conditions: [];
  };
  previewId: string;
  processors: [
    {
      processorClass: string;
      properties: Property;
    },
  ];
  referer: string;
  subtitle: string;
  target: null;
  text: string;
  title: string;
  type: string;
  uploadToBlobStorage: boolean;
  validFields: string[];
  validationRules: {
    rules: Rule[];
  };
};

type FilterOption = {
  value: string;
  count: number;
};

export type Download = {
  datasource: string;
  date: string;
  id: string;
  meta: { [key: string]: string[] };
  size: number;
  snippet: string[];
  title: string;
  url: LinkBase;
  amount?: number;
};

export type Order = Download & {
  amount: number;
};

// DLC - Cart props
export type CartProps = {
  updateDownload: (download: Download) => void;
  updateOrder: (order: Order) => void;
  updateAmount: (order: Order) => void;
  downloadZip: (downloads: Download[]) => void;
  getSessionStorage: (key: string) => string;
  downloadProgress: number;
  setDownloadProgress: (progress: number) => void;
  orders?: Order[];
  downloads?: Download[];
  downloadForm?: Form[] | Content[];
};

export type CartOrdersProps = CartProps & {
  orders: Order[];
  form: Form | Content;
  onFinished?: () => void;
};

// DLC - Tile props
export type TilesProps = {
  data: Download;
  updateOrder: (order: Order) => void;
  updateDownload: (download: Download) => void;
  downloads: Download[];
  dateFormat: string;
  hasOrder: boolean;
};

export type TilesAreaProps = TilesProps & {
  results: Download[];
  orders: Order[];
  handleLoadMore: () => void;
  lastPage: boolean;
  containerRef: Ref<HTMLDivElement> | undefined;
};

// DLC Country
export type CartDownloadsProps = CartProps & {
  downloads: Download[];
  downloadZipHandler: () => void;
};

export type AdditionalContentProps = {
  preFilters?: string[];
};

export type AdditionalPageProps = {
  disableSearchbar?: boolean;
  disableUserFilters?: boolean;
  preFilters?: string[];
};

export type DownloadCenterContainerProps = {
  initialData: { pages: Data[] };
};

// DLC Group
export type CartOrdersPropsGroup = CartProps & {
  orders: Order[];
  form: Form;
  type: 'download' | 'order';
  onFinished?: () => void;
  downloadAsZip: () => void;
};

export type BasketBoxProps = {
  orderCounter: number;
  downloadCounter: number;
  setShowLightbox: (lightbox: string | undefined) => void;
  showLightbox?: string;
};

export type DownloadcenterProps = {
  page: Content['page'] & {
    filterTypeOrder: Record<string, number>;
    filterOrder: Record<string, number>;
    filterMediaOrder: Record<string, number>;
    mediaFilter: string;
  };
  breadcrumbs: Content['breadcrumbs'];
  contentAreas: Content['contentAreas'];
};

export type DownloadcenterCategoryProps = {
  items: Download[];
  updateDownloads: (item: Download) => void;
  updateOrders: (item: Order) => void;
  categoryName: string;
  downloads: Download[];
  orders: Order[];
};

export type DownloadcenterLightboxProps = CartOrdersPropsGroup & {
  downloads: Download[];
  handleClose: () => void;
  updateDownloads: (download: Download) => void;
};

export type DownloadcenterListItemProps = DownloadcenterCategoryProps & {
  item: Download;
  setOrderCounter: () => void;
  setDownloadCounter: () => void;
};

export type DownloadcenterMediaSectionProps = DownloadcenterCategoryProps & {
  getFilter4Items: () => SelectOption[];
  handleChangeFilter4: (option: SelectOption | SelectOption[]) => void;
  filter4Value: string;
};

export type DownloadcenterMediaTileProps = DownloadcenterListItemProps & {
  setDownloadCounter: (counter: number) => void;
};

export type FilterItems = { value: string; text: string }[];

export type FilterAreaProps = {
  getFilter2Items: Function;
  getFilter1Items: Function;
  handleFilterChange: (filter1: string, filter2: string[]) => void;
  resetFilterCallback: Function;
  filter1Value: string;
  filter2Value: string[];
  setFilter1Value: (value: string) => void;
  setFilter2Value: (value: string[]) => void;
  remainingFilter2Items: string[];
  setRemainingFilter2Items: (value: string[]) => void;
  setRemainingF2Items: (value: string[]) => void;
  filterAreaDisabled: boolean;
};

// DLC Nord
export type CartNordicsProps = CartProps & {
  translations: { [key: string]: string };
  setDownloadProgress: React.Dispatch<React.SetStateAction<number>>;
};

export type DownloadCenterContainerNordProps = {
  page: Content['page'] & {
    preFilters?: string[];
    disableSearchbar?: boolean;
    disableUserFilters?: boolean;
  };
  contentAreas: Content['contentAreas'];
  initialData: { pages: Data[] };
};

export type LightboxNordicsProps = CartProps & {
  isFinished: boolean;
  openDownloads: boolean;
  openOrders: boolean;
  downloads: Download[];
  handleClose: () => void;
  handleDownloadChange: (product: Download) => void;
  handleDownloadZip: () => void;
  orders: Order[];
  form: Form | Content;
  handleFinished: () => void;
};

export type FilterNordProps = {
  activeItems: string[];
  filterKey: string;
  options: FilterOption[];
  handleFilterChange: (filterKey: string, option: SelectOption[]) => void;
};

export type FilterAreaNordProps = {
  data: { [key: string]: FilterOption[] };
  handleFilterChange: (filterKey: string, option: SelectOption[]) => void;
  activeFilters?: { [key: string]: string[] };
};

export type SorterAreaNordProps = {
  totalResults: number;
  handleSortChange?: (option: SelectOption | SelectOption[]) => void;
};

export const mapFileToIcon = {
  mp4: 'mp4',
  mp3: 'mp3',
  xlsx: 'xls',
  pdf: 'pdf',
  rfa: 'zip',
  zip: 'zip',
  jpeg: 'image',
  jpg: 'image',
  gif: 'image',
  png: 'image',
};
